import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const ConfigurationPanel = ({ activeTab }) => {
  const [expandedSections, setExpandedSections] = useState({});
  const [formData, setFormData] = useState({
    sessionTimeout: '30',
    maxLoginAttempts: '5',
    passwordExpiry: '90',
    shiftDuration: '8',
    overtimeThreshold: '40',
    notificationEmail: true,
    notificationSms: false,
    autoScheduling: true,
    requireApproval: true
  });

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev?.[sectionId]
    }));
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const renderAuthenticationSettings = () => (
    <div className="space-y-6">
      <div className="bg-card border border-border rounded-lg">
        <button
          onClick={() => toggleSection('oauth')}
          className="w-full flex items-center justify-between p-4 text-left hover:bg-muted transition-colors"
        >
          <div className="flex items-center space-x-3">
            <Icon name="Shield" size={20} className="text-primary" />
            <div>
              <h3 className="font-medium text-foreground">Google OAuth Configuration</h3>
              <p className="text-sm text-muted-foreground">Manage authentication provider settings</p>
            </div>
          </div>
          <Icon 
            name={expandedSections?.oauth ? "ChevronUp" : "ChevronDown"} 
            size={20} 
            className="text-muted-foreground" 
          />
        </button>
        
        {expandedSections?.oauth && (
          <div className="p-4 border-t border-border space-y-4">
            <Input
              label="Client ID"
              type="text"
              placeholder="Google OAuth Client ID"
              value="1234567890-abcdefghijklmnopqrstuvwxyz.apps.googleusercontent.com"
              disabled
              description="Contact IT administrator to modify OAuth credentials"
            />
            <Input
              label="Authorized Domains"
              type="text"
              placeholder="company.com, *.company.com"
              value="company.com, *.company.com"
            />
            <div className="flex items-center justify-between p-3 bg-success/10 border border-success/20 rounded-lg">
              <div className="flex items-center space-x-2">
                <Icon name="CheckCircle" size={16} className="text-success" />
                <span className="text-sm text-success">OAuth connection active</span>
              </div>
              <Button variant="outline" size="sm">Test Connection</Button>
            </div>
          </div>
        )}
      </div>

      <div className="bg-card border border-border rounded-lg">
        <button
          onClick={() => toggleSection('security')}
          className="w-full flex items-center justify-between p-4 text-left hover:bg-muted transition-colors"
        >
          <div className="flex items-center space-x-3">
            <Icon name="Lock" size={20} className="text-primary" />
            <div>
              <h3 className="font-medium text-foreground">Security Policies</h3>
              <p className="text-sm text-muted-foreground">Configure authentication and access controls</p>
            </div>
          </div>
          <Icon 
            name={expandedSections?.security ? "ChevronUp" : "ChevronDown"} 
            size={20} 
            className="text-muted-foreground" 
          />
        </button>
        
        {expandedSections?.security && (
          <div className="p-4 border-t border-border space-y-4">
            <Input
              label="Session Timeout (minutes)"
              type="number"
              value={formData?.sessionTimeout}
              onChange={(e) => handleInputChange('sessionTimeout', e?.target?.value)}
              description="Automatic logout after inactivity"
            />
            <Input
              label="Maximum Login Attempts"
              type="number"
              value={formData?.maxLoginAttempts}
              onChange={(e) => handleInputChange('maxLoginAttempts', e?.target?.value)}
              description="Account lockout threshold"
            />
            <Input
              label="Password Expiry (days)"
              type="number"
              value={formData?.passwordExpiry}
              onChange={(e) => handleInputChange('passwordExpiry', e?.target?.value)}
              description="Force password reset interval"
            />
          </div>
        )}
      </div>
    </div>
  );

  const renderSchedulingRules = () => (
    <div className="space-y-6">
      <div className="bg-card border border-border rounded-lg">
        <button
          onClick={() => toggleSection('shifts')}
          className="w-full flex items-center justify-between p-4 text-left hover:bg-muted transition-colors"
        >
          <div className="flex items-center space-x-3">
            <Icon name="Clock" size={20} className="text-primary" />
            <div>
              <h3 className="font-medium text-foreground">Shift Configuration</h3>
              <p className="text-sm text-muted-foreground">Define shift duration and scheduling rules</p>
            </div>
          </div>
          <Icon 
            name={expandedSections?.shifts ? "ChevronUp" : "ChevronDown"} 
            size={20} 
            className="text-muted-foreground" 
          />
        </button>
        
        {expandedSections?.shifts && (
          <div className="p-4 border-t border-border space-y-4">
            <Select
              label="Standard Shift Duration"
              options={[
                { value: '4', label: '4 hours' },
                { value: '6', label: '6 hours' },
                { value: '8', label: '8 hours' },
                { value: '12', label: '12 hours' }
              ]}
              value={formData?.shiftDuration}
              onChange={(value) => handleInputChange('shiftDuration', value)}
            />
            <Input
              label="Minimum Break Between Shifts (hours)"
              type="number"
              value="8"
              onChange={(e) => handleInputChange('minBreak', e?.target?.value)}
              description="Required rest period between consecutive shifts"
            />
            <Input
              label="Maximum Consecutive Days"
              type="number"
              value="6"
              onChange={(e) => handleInputChange('maxConsecutive', e?.target?.value)}
              description="Maximum days an employee can work consecutively"
            />
          </div>
        )}
      </div>

      <div className="bg-card border border-border rounded-lg">
        <button
          onClick={() => toggleSection('overtime')}
          className="w-full flex items-center justify-between p-4 text-left hover:bg-muted transition-colors"
        >
          <div className="flex items-center space-x-3">
            <Icon name="TrendingUp" size={20} className="text-primary" />
            <div>
              <h3 className="font-medium text-foreground">Overtime Policies</h3>
              <p className="text-sm text-muted-foreground">Configure overtime calculation and limits</p>
            </div>
          </div>
          <Icon 
            name={expandedSections?.overtime ? "ChevronUp" : "ChevronDown"} 
            size={20} 
            className="text-muted-foreground" 
          />
        </button>
        
        {expandedSections?.overtime && (
          <div className="p-4 border-t border-border space-y-4">
            <Input
              label="Weekly Overtime Threshold (hours)"
              type="number"
              value={formData?.overtimeThreshold}
              onChange={(e) => handleInputChange('overtimeThreshold', e?.target?.value)}
              description="Hours per week before overtime rates apply"
            />
            <Select
              label="Overtime Rate Multiplier"
              options={[
                { value: '1.25', label: '1.25x (25% premium)' },
                { value: '1.5', label: '1.5x (50% premium)' },
                { value: '2.0', label: '2.0x (100% premium)' }
              ]}
              value="1.5"
              onChange={(value) => handleInputChange('overtimeRate', value)}
            />
            <Checkbox
              label="Require manager approval for overtime"
              checked={formData?.requireApproval}
              onChange={(e) => handleInputChange('requireApproval', e?.target?.checked)}
            />
          </div>
        )}
      </div>
    </div>
  );

  const renderNotificationSettings = () => (
    <div className="space-y-6">
      <div className="bg-card border border-border rounded-lg">
        <button
          onClick={() => toggleSection('notifications')}
          className="w-full flex items-center justify-between p-4 text-left hover:bg-muted transition-colors"
        >
          <div className="flex items-center space-x-3">
            <Icon name="Bell" size={20} className="text-primary" />
            <div>
              <h3 className="font-medium text-foreground">Notification Channels</h3>
              <p className="text-sm text-muted-foreground">Configure how users receive notifications</p>
            </div>
          </div>
          <Icon 
            name={expandedSections?.notifications ? "ChevronUp" : "ChevronDown"} 
            size={20} 
            className="text-muted-foreground" 
          />
        </button>
        
        {expandedSections?.notifications && (
          <div className="p-4 border-t border-border space-y-4">
            <Checkbox
              label="Email Notifications"
              description="Send notifications via email"
              checked={formData?.notificationEmail}
              onChange={(e) => handleInputChange('notificationEmail', e?.target?.checked)}
            />
            <Checkbox
              label="SMS Notifications"
              description="Send notifications via SMS (requires SMS service setup)"
              checked={formData?.notificationSms}
              onChange={(e) => handleInputChange('notificationSms', e?.target?.checked)}
            />
            <Input
              label="SMTP Server"
              type="text"
              placeholder="smtp.company.com"
              value="smtp.company.com"
              description="Email server configuration"
            />
            <Input
              label="From Email Address"
              type="email"
              placeholder="noreply@company.com"
              value="noreply@company.com"
              description="Sender address for system emails"
            />
          </div>
        )}
      </div>

      <div className="bg-card border border-border rounded-lg">
        <button
          onClick={() => toggleSection('templates')}
          className="w-full flex items-center justify-between p-4 text-left hover:bg-muted transition-colors"
        >
          <div className="flex items-center space-x-3">
            <Icon name="FileText" size={20} className="text-primary" />
            <div>
              <h3 className="font-medium text-foreground">Notification Templates</h3>
              <p className="text-sm text-muted-foreground">Customize notification messages</p>
            </div>
          </div>
          <Icon 
            name={expandedSections?.templates ? "ChevronUp" : "ChevronDown"} 
            size={20} 
            className="text-muted-foreground" 
          />
        </button>
        
        {expandedSections?.templates && (
          <div className="p-4 border-t border-border space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Shift Assignment Template</label>
              <textarea
                className="w-full p-3 border border-border rounded-lg resize-none"
                rows={3}
                value={`Hallo {employee_name},\n\nIhnen wurde eine neue Schicht zugewiesen:\nDatum: {shift_date}\nZeit: {shift_time}\nStandort: {location}\n\nBitte bestätigen Sie Ihre Verfügbarkeit.`}
                onChange={() => {}}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Schedule Change Template</label>
              <textarea
                className="w-full p-3 border border-border rounded-lg resize-none"
                rows={3}
                value={`Hallo {employee_name},\n\nIhr Schichtplan wurde geändert:\nAlte Zeit: {old_time}\nNeue Zeit: {new_time}\n\nBitte überprüfen Sie Ihren aktualisierten Zeitplan.`}
                onChange={() => {}}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderIntegrationSettings = () => (
    <div className="space-y-6">
      <div className="bg-card border border-border rounded-lg">
        <button
          onClick={() => toggleSection('apis')}
          className="w-full flex items-center justify-between p-4 text-left hover:bg-muted transition-colors"
        >
          <div className="flex items-center space-x-3">
            <Icon name="Zap" size={20} className="text-primary" />
            <div>
              <h3 className="font-medium text-foreground">API Integrations</h3>
              <p className="text-sm text-muted-foreground">Manage external system connections</p>
            </div>
          </div>
          <Icon 
            name={expandedSections?.apis ? "ChevronUp" : "ChevronDown"} 
            size={20} 
            className="text-muted-foreground" 
          />
        </button>
        
        {expandedSections?.apis && (
          <div className="p-4 border-t border-border space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 border border-border rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-foreground">Google Calendar</h4>
                  <div className="flex items-center space-x-1">
                    <div className="w-2 h-2 bg-success rounded-full"></div>
                    <span className="text-xs text-success">Connected</span>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mb-3">Sync shifts with Google Calendar</p>
                <Button variant="outline" size="sm" fullWidth>Configure</Button>
              </div>
              
              <div className="p-4 border border-border rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-foreground">Payroll System</h4>
                  <div className="flex items-center space-x-1">
                    <div className="w-2 h-2 bg-warning rounded-full"></div>
                    <span className="text-xs text-warning">Pending</span>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mb-3">Export timesheet data</p>
                <Button variant="outline" size="sm" fullWidth>Setup</Button>
              </div>
            </div>
            
            <Input
              label="API Rate Limit (requests/minute)"
              type="number"
              value="100"
              description="Maximum API requests per minute per client"
            />
          </div>
        )}
      </div>
    </div>
  );

  const renderSecuritySettings = () => (
    <div className="space-y-6">
      <div className="bg-card border border-border rounded-lg">
        <button
          onClick={() => toggleSection('audit')}
          className="w-full flex items-center justify-between p-4 text-left hover:bg-muted transition-colors"
        >
          <div className="flex items-center space-x-3">
            <Icon name="FileSearch" size={20} className="text-primary" />
            <div>
              <h3 className="font-medium text-foreground">Audit & Logging</h3>
              <p className="text-sm text-muted-foreground">Configure system audit trails</p>
            </div>
          </div>
          <Icon 
            name={expandedSections?.audit ? "ChevronUp" : "ChevronDown"} 
            size={20} 
            className="text-muted-foreground" 
          />
        </button>
        
        {expandedSections?.audit && (
          <div className="p-4 border-t border-border space-y-4">
            <Select
              label="Log Retention Period"
              options={[
                { value: '30', label: '30 days' },
                { value: '90', label: '90 days' },
                { value: '365', label: '1 year' },
                { value: '730', label: '2 years' }
              ]}
              value="365"
              description="How long to keep audit logs"
            />
            <Checkbox
              label="Log user login/logout events"
              checked
              onChange={() => {}}
            />
            <Checkbox
              label="Log shift modifications"
              checked
              onChange={() => {}}
            />
            <Checkbox
              label="Log administrative actions"
              checked
              onChange={() => {}}
            />
          </div>
        )}
      </div>

      <div className="bg-card border border-border rounded-lg">
        <button
          onClick={() => toggleSection('backup')}
          className="w-full flex items-center justify-between p-4 text-left hover:bg-muted transition-colors"
        >
          <div className="flex items-center space-x-3">
            <Icon name="Database" size={20} className="text-primary" />
            <div>
              <h3 className="font-medium text-foreground">Backup & Recovery</h3>
              <p className="text-sm text-muted-foreground">Data backup configuration</p>
            </div>
          </div>
          <Icon 
            name={expandedSections?.backup ? "ChevronUp" : "ChevronDown"} 
            size={20} 
            className="text-muted-foreground" 
          />
        </button>
        
        {expandedSections?.backup && (
          <div className="p-4 border-t border-border space-y-4">
            <Select
              label="Backup Frequency"
              options={[
                { value: 'daily', label: 'Daily' },
                { value: 'weekly', label: 'Weekly' },
                { value: 'monthly', label: 'Monthly' }
              ]}
              value="daily"
            />
            <Input
              label="Backup Retention (days)"
              type="number"
              value="30"
              description="Number of backup copies to retain"
            />
            <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
              <div>
                <p className="text-sm font-medium text-foreground">Last Backup</p>
                <p className="text-xs text-muted-foreground">03.10.2025 um 02:00 Uhr</p>
              </div>
              <Button variant="outline" size="sm">Backup Now</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const getTabContent = () => {
    switch (activeTab) {
      case 'authentication':
        return renderAuthenticationSettings();
      case 'scheduling':
        return renderSchedulingRules();
      case 'notifications':
        return renderNotificationSettings();
      case 'integrations':
        return renderIntegrationSettings();
      case 'security':
        return renderSecuritySettings();
      default:
        return renderAuthenticationSettings();
    }
  };

  return (
    <div className="flex-1 p-6 overflow-y-auto">
      <div className="max-w-4xl mx-auto">
        {getTabContent()}
        
        <div className="mt-8 flex items-center justify-between p-4 bg-muted rounded-lg">
          <div className="flex items-center space-x-2">
            <Icon name="AlertTriangle" size={16} className="text-warning" />
            <span className="text-sm text-foreground">Unsaved changes detected</span>
          </div>
          <div className="flex space-x-2">
            <Button variant="outline" size="sm">Discard</Button>
            <Button variant="default" size="sm">Save Changes</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfigurationPanel;